import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCompleteGithubInstall } from '@fluxter/shared';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthHeader, AuthShell } from '@/components/AuthShell';

const GITHUB_INSTALL_STATE_KEY = 'fluxter:github-install-state';
const GITHUB_INSTALL_WORKSPACE_KEY = 'fluxter:github-install-workspace';

interface CallbackSearchParams {
  installation_id?: string;
  state?: string;
  setup_action?: string;
}

/**
 * Slice 11 — landing page after GitHub redirects the user back from
 * the App-install screen. Reads `installation_id` + `state` from the
 * URL, verifies the state round-tripped unmodified against what we
 * stashed in `localStorage` before opening the install URL, and POSTs
 * to our install-callback endpoint to persist the row.
 *
 * **Workspace-agnostic route** (fix/github-setup-url-workspace-agnostic):
 * a single GitHub App can configure only one Setup URL, so the route
 * lives at `/settings/github/callback` (top-level, no workspace slug
 * in the path). The workspace the user was on when they clicked
 * Install gets stashed in `localStorage` alongside the state token,
 * read here, and used to navigate them back to the right
 * `/w/{slug}/settings` after success. If the workspace slug isn't
 * available (storage cleared, opened cold, etc.) we fall back to
 * `/` and let the index route's redirect logic land them on their
 * default workspace.
 *
 * **localStorage** rather than `sessionStorage`: GitHub's install
 * page opens in a new tab. `sessionStorage` inheritance across new
 * tabs is browser-dependent (works in Chrome/Firefox but not Safari
 * in some configurations). `localStorage` is unconditionally shared
 * across same-origin tabs, so the handoff is reliable. Cleaned up
 * on every exit path so stale entries don't accumulate.
 */
export const GithubInstallCallbackPage = () => {
  const search = useSearch({ from: '/settings/github/callback' }) as CallbackSearchParams;
  const navigate = useNavigate();
  const completeInstall = useCompleteGithubInstall();

  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [returnWorkspace, setReturnWorkspace] = useState<string | null>(null);

  // React StrictMode runs effects twice in development. Without a
  // guard, we'd POST the install-callback twice — the second call
  // can race with the first (especially in the discovery-fallback
  // path) and clobber the success UI with a transient error before
  // the navigation timer fires. The ref ensures the mutation runs
  // exactly once per mount.
  const hasDispatchedRef = useRef(false);

  useEffect(() => {
    if (hasDispatchedRef.current) {
      return;
    }
    hasDispatchedRef.current = true;
    const installationIdRaw = search.installation_id;
    const stateFromUrl = search.state;
    const stashedState = localStorage.getItem(GITHUB_INSTALL_STATE_KEY);
    const stashedWorkspace = localStorage.getItem(GITHUB_INSTALL_WORKSPACE_KEY);

    setReturnWorkspace(stashedWorkspace);

    /**
     * Single cleanup helper — every exit path (success, validation
     * error, async failure) calls this so localStorage doesn't keep
     * stale tokens around if the user abandons the flow.
     */
    const clearHandoff = (): void => {
      localStorage.removeItem(GITHUB_INSTALL_STATE_KEY);
      localStorage.removeItem(GITHUB_INSTALL_WORKSPACE_KEY);
    };

    // The state token must be present in BOTH the URL (round-tripped
    // through GitHub) and our local storage (we stashed it before
    // opening the install URL). Mismatch implies the user opened this
    // page out of band, or the redirect was tampered with.
    //
    // `installation_id` is OPTIONAL on the redirect: GitHub usually
    // appends it but some App configurations cause it to be omitted.
    // When missing, we forward state-only and let the API discover
    // the installation via the App installations endpoint scoped to
    // the state's issuance window.
    if (! stateFromUrl) {
      setStatus('error');
      setErrorMessage('Missing state in the redirect.');
      clearHandoff();
      return;
    }
    if (stashedState !== stateFromUrl) {
      setStatus('error');
      setErrorMessage('Install handoff token mismatch. Restart the install flow from Workspace settings.');
      clearHandoff();
      return;
    }

    let installationId: number | undefined;
    if (installationIdRaw) {
      const parsed = Number.parseInt(installationIdRaw, 10);
      if (! Number.isFinite(parsed) || parsed <= 0) {
        setStatus('error');
        setErrorMessage('Malformed installation_id.');
        clearHandoff();
        return;
      }
      installationId = parsed;
    }

    void (async () => {
      try {
        await completeInstall.mutateAsync({
          ...(installationId !== undefined ? { installation_id: installationId } : {}),
          state: stateFromUrl,
        });
        clearHandoff();
        setStatus('success');
        // Brief pause so the user sees the success state before we redirect.
        setTimeout(() => {
          if (stashedWorkspace) {
            void navigate({
              to: '/w/$workspaceSlug/settings',
              params: { workspaceSlug: stashedWorkspace },
            });
          } else {
            // No remembered workspace — let the index route's redirect
            // logic land the user on their default workspace home.
            void navigate({ to: '/' });
          }
        }, 1200);
      } catch (e) {
        setStatus('error');
        setErrorMessage(e instanceof Error ? e.message : 'Could not finalise the install.');
        clearHandoff();
      }
    })();
    // Run once on mount; the deps are deliberately the URL params + the
    // mutation handle, all of which are stable for a given navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = (): void => {
    if (returnWorkspace) {
      void navigate({
        to: '/w/$workspaceSlug/settings',
        params: { workspaceSlug: returnWorkspace },
      });
    } else {
      void navigate({ to: '/' });
    }
  };

  return (
    <AuthShell
      width="md"
      header={
        <AuthHeader
          title="GitHub install"
          subtitle={
            status === 'pending'
              ? 'Finalising install with GitHub.'
              : status === 'success'
                ? 'Workspace connected — redirecting you back.'
                : 'We could not finish the install.'
          }
        />
      }
    >
      {status === 'pending' ? (
        <p className="text-foreground-muted flex items-center justify-center gap-2 text-[12.5px]">
          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          Verifying installation
        </p>
      ) : null}

      {status === 'success' ? (
        <p className="text-foreground text-center text-[13px]">
          GitHub connected. Redirecting…
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="space-y-4 text-center">
          <p className="text-destructive text-[12.5px]" role="alert">
            {errorMessage}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="w-full"
          >
            {returnWorkspace ? 'Back to workspace settings' : 'Back to home'}
          </Button>
        </div>
      ) : null}
    </AuthShell>
  );
};
